import React, { useState } from 'react';
import styled from 'styled-components';

interface Column<T> {
  key: keyof T | string;
  title: string;
  width?: string;
  render?: (value: any, record: T) => React.ReactNode;
}

interface DataTableProps<T> {
  data: T[];
  columns: Column<T>[];
  loading?: boolean;
  onRowClick?: (record: T) => void;
  pagination?: {
    current: number;
    pageSize: number;
    total: number;
    onChange: (page: number, pageSize: number) => void;
  };
  searchPlaceholder?: string;
  onSearch?: (searchTerm: string) => void;
  actions?: React.ReactNode;
}

const TableContainer = styled.div`
  background: var(--surface-color);
  border-radius: var(--border-radius-md);
  box-shadow: var(--shadow-light);
  overflow: hidden;
`;

const TableHeader = styled.div`
  padding: 16px 20px;
  border-bottom: 1px solid var(--border-color);
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 16px;

  @media (max-width: 767px) {
    flex-direction: column;
    align-items: stretch;
    gap: 12px;
  }
`;

const SearchContainer = styled.div`
  position: relative;
  flex: 1;
  max-width: 400px;

  @media (max-width: 767px) {
    max-width: none;
  }
`;

const SearchInput = styled.input`
  width: 100%;
  padding: 10px 16px;
  border: 1px solid var(--border-color);
  border-radius: var(--border-radius-sm);
  font-size: 14px;
  background: var(--surface-color);

  &:focus {
    outline: none;
    border-color: var(--primary-color);
    box-shadow: 0 0 0 2px var(--primary-light);
  }

  &::placeholder {
    color: var(--text-tertiary);
  }
`;

const ActionsContainer = styled.div`
  display: flex;
  gap: 8px;
  align-items: center;

  @media (max-width: 767px) {
    justify-content: stretch;
  }
`;

const Table = styled.table`
  width: 100%;
  border-collapse: collapse;
`;

const TableHead = styled.thead`
  background: var(--background-color);
`;

const TableBody = styled.tbody``;

const TableRow = styled.tr<{ $clickable?: boolean }>`
  border-bottom: 1px solid var(--border-color-light);
  transition: background 0.15s ease;

  &:hover {
    background: rgba(240, 243, 255, 0.5);
  }

  ${props => props.$clickable && `
    cursor: pointer;
  `}

  &:last-child {
    border-bottom: none;
  }
`;

const TableHeaderCell = styled.th<{ width?: string }>`
  padding: 16px 20px;
  text-align: center;
  font-weight: 600;
  color: var(--text-primary);
  font-size: 14px;
  width: ${props => props.width || 'auto'};
`;

const TableCell = styled.td`
  padding: 16px 20px;
  text-align: center;
  color: var(--text-primary);
  font-size: 14px;
  vertical-align: middle;
`;

const LoadingContainer = styled.div`
  padding: 40px;
  text-align: center;
  color: var(--text-secondary);
`;

const EmptyContainer = styled.div`
  padding: 40px;
  text-align: center;
  color: var(--text-secondary);
`;

const PaginationContainer = styled.div`
  padding: 16px 20px;
  border-top: 1px solid var(--border-color);
  display: flex;
  justify-content: space-between;
  align-items: center;
  
  @media (max-width: 767px) {
    flex-direction: column;
    gap: 12px;
    align-items: stretch;
  }
`;

const PaginationInfo = styled.div`
  color: var(--text-secondary);
  font-size: 14px;
`;

const PaginationButtons = styled.div`
  display: flex;
  gap: 8px;
  align-items: center;
`;

const PaginationButton = styled.button<{ $active?: boolean }>`
  padding: 8px 12px;
  border: 1px solid var(--border-color);
  border-radius: var(--border-radius-sm);
  background: ${props => props.$active ? 'var(--primary-color)' : 'var(--surface-color)'};
  color: ${props => props.$active ? 'white' : 'var(--text-primary)'};
  cursor: pointer;
  font-size: 14px;

  &:hover:not(:disabled) {
    background: ${props => props.$active ? 'var(--primary-hover)' : 'var(--hover-background)'};
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;

function DataTable<T extends Record<string, any>>({
  data,
  columns,
  loading = false,
  onRowClick,
  pagination,
  searchPlaceholder = "חפש...",
  onSearch,
  actions
}: DataTableProps<T>) {
  const [searchTerm, setSearchTerm] = useState('');

  const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setSearchTerm(value);
    if (onSearch) {
      onSearch(value);
    }
  };

  const renderCellValue = (record: T, column: Column<T>) => {
    if (column.render) {
      return column.render(record[column.key as keyof T], record);
    }
    return record[column.key as keyof T];
  };

  const renderPagination = () => {
    if (!pagination) return null;

    const { current, pageSize, total, onChange } = pagination;
    const totalPages = Math.ceil(total / pageSize);
    const startItem = (current - 1) * pageSize + 1;
    const endItem = Math.min(current * pageSize, total);

    const pages = [];
    const maxVisiblePages = 5;
    
    let startPage = Math.max(1, current - Math.floor(maxVisiblePages / 2));
    let endPage = Math.min(totalPages, startPage + maxVisiblePages - 1);
    
    if (endPage - startPage + 1 < maxVisiblePages) {
      startPage = Math.max(1, endPage - maxVisiblePages + 1);
    }

    for (let i = startPage; i <= endPage; i++) {
      pages.push(i);
    }

    return (
      <PaginationContainer>
        <PaginationInfo>
          מציג {startItem}-{endItem} מתוך {total} פריטים
        </PaginationInfo>
        <PaginationButtons>
          <PaginationButton
            onClick={() => onChange(current - 1, pageSize)}
            disabled={current <= 1}
          >
            הקודם
          </PaginationButton>
          {pages.map(page => (
            <PaginationButton
              key={page}
              $active={page === current}
              onClick={() => onChange(page, pageSize)}
            >
              {page}
            </PaginationButton>
          ))}
          <PaginationButton
            onClick={() => onChange(current + 1, pageSize)}
            disabled={current >= totalPages}
          >
            הבא
          </PaginationButton>
        </PaginationButtons>
      </PaginationContainer>
    );
  };

  return (
    <TableContainer>
      {(onSearch || actions) && (
        <TableHeader>
          {onSearch && (
            <SearchContainer>
              <SearchInput
                type="text"
                placeholder={searchPlaceholder}
                value={searchTerm}
                onChange={handleSearchChange}
              />
            </SearchContainer>
          )}
          {actions && (
            <ActionsContainer>
              {actions}
            </ActionsContainer>
          )}
        </TableHeader>
      )}
      
      {loading ? (
        <LoadingContainer>
          טוען...
        </LoadingContainer>
      ) : data.length === 0 ? (
        <EmptyContainer>
          אין נתונים להצגה
        </EmptyContainer>
      ) : (
        <Table>
          <TableHead>
            <TableRow>
              {columns.map((column, index) => (
                <TableHeaderCell key={index} width={column.width}>
                  {column.title}
                </TableHeaderCell>
              ))}
            </TableRow>
          </TableHead>
          <TableBody>
            {data.map((record, index) => (
              <TableRow
                key={index}
                $clickable={!!onRowClick}
                onClick={() => onRowClick?.(record)}
              >
                {columns.map((column, colIndex) => (
                  <TableCell key={colIndex}>
                    {renderCellValue(record, column)}
                  </TableCell>
                ))}
              </TableRow>
            ))}
          </TableBody>
        </Table>
      )}
      
      {renderPagination()}
    </TableContainer>
  );
}

export default DataTable;